import React from 'react';
import { Card, CardContent } from '../ui/Card';
import { formatCurrency } from '../../lib/utils';
import { FlameIcon, ClockIcon, CalendarIcon, TrendingUpIcon, ZapIcon, UsersIcon } from 'lucide-react';
import type { PeakDay, PeakHour, ChannelBreakdown } from '../../lib/metrics-hooks';
interface MetricsPeakInsightsProps {
  peakDays: PeakDay[];
  peakHours: PeakHour[];
  channelBreakdown: ChannelBreakdown;
  totalOrders: number;
  totalRevenue: number;
  averageTicket: number;
}
export function MetricsPeakInsights({
  peakDays,
  peakHours,
  channelBreakdown,
  totalOrders,
  totalRevenue,
  averageTicket
}: MetricsPeakInsightsProps) {
  const totalChannel = channelBreakdown.qr + channelBreakdown.waiter + channelBreakdown.other;
  const qrPct = totalChannel > 0 ? Math.round(channelBreakdown.qr / totalChannel * 100) : 0;
  const waiterPct = totalChannel > 0 ? Math.round(channelBreakdown.waiter / totalChannel * 100) : 0;
  return <div className="space-y-4" data-id="element-676">
      {/* Summary Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-677">
        <Card data-id="element-678">
          <CardContent className="p-5" data-id="element-679">
            <div className="flex items-start gap-3" data-id="element-680">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-681">
                <TrendingUpIcon className="w-5 h-5 text-emerald-600" data-id="element-682" />
              </div>
              <div className="min-w-0" data-id="element-683">
                <p className="text-sm text-gray-500 font-medium" data-id="element-684">
                  Ingresos Totales
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-685">
                  {formatCurrency(totalRevenue)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-686">
          <CardContent className="p-5" data-id="element-687">
            <div className="flex items-start gap-3" data-id="element-688">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-689">
                <UsersIcon className="w-5 h-5 text-blue-600" data-id="element-690" />
              </div>
              <div className="min-w-0" data-id="element-691">
                <p className="text-sm text-gray-500 font-medium" data-id="element-692">
                  Total Órdenes
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5" data-id="element-693">
                  {totalOrders}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-694">
          <CardContent className="p-5" data-id="element-695">
            <div className="flex items-start gap-3" data-id="element-696">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-697">
                <ZapIcon className="w-5 h-5 text-purple-600" data-id="element-698" />
              </div>
              <div className="min-w-0" data-id="element-699">
                <p className="text-sm text-gray-500 font-medium" data-id="element-700">
                  Ticket Promedio
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-701">
                  {formatCurrency(averageTicket)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Peak Insights Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-702">
        {/* Peak Day */}
        <Card data-id="element-703">
          <CardContent className="p-5" data-id="element-704">
            <div className="flex items-center gap-2 mb-3" data-id="element-705">
              <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center" data-id="element-706">
                <FlameIcon className="w-4 h-4 text-red-500" data-id="element-707" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-708">
                Día Pico
              </span>
            </div>
            {peakDays.length > 0 ? <div className="space-y-2" data-id="element-709">
                {peakDays.map((day, i) => <div key={day.date} className="flex items-center justify-between" data-id="element-710">
                    <div className="flex items-center gap-2" data-id="element-711">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-712">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-713">{day.label}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-714">
                      {day.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-715">Sin datos</p>}
          </CardContent>
        </Card>

        {/* Peak Hour */}
        <Card data-id="element-716">
          <CardContent className="p-5" data-id="element-717">
            <div className="flex items-center gap-2 mb-3" data-id="element-718">
              <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center" data-id="element-719">
                <ClockIcon className="w-4 h-4 text-amber-600" data-id="element-720" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-721">
                Hora Pico
              </span>
            </div>
            {peakHours.length > 0 ? <div className="space-y-2" data-id="element-722">
                {peakHours.map((hour, i) => <div key={hour.hour} className="flex items-center justify-between" data-id="element-723">
                    <div className="flex items-center gap-2" data-id="element-724">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-725">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-726">
                        {hour.label}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-727">
                      {hour.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-728">Sin datos</p>}
          </CardContent>
        </Card>

        {/* QR Self-service */}
        <Card data-id="element-729">
          <CardContent className="p-5" data-id="element-730">
            <div className="flex items-center gap-2 mb-3" data-id="element-731">
              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-732">
                <CalendarIcon className="w-4 h-4 text-emerald-600" data-id="element-733" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-734">
                Autoservicio QR
              </span>
            </div>
            <div className="space-y-1" data-id="element-735">
              <p className="text-2xl font-bold text-emerald-600" data-id="element-736">
                {channelBreakdown.qr}
              </p>
              <p className="text-sm text-gray-500" data-id="element-737">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-738">{qrPct}%</span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-739">
                {formatCurrency(channelBreakdown.qrRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Waiter */}
        <Card data-id="element-740">
          <CardContent className="p-5" data-id="element-741">
            <div className="flex items-center gap-2 mb-3" data-id="element-742">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-743">
                <UsersIcon className="w-4 h-4 text-blue-600" data-id="element-744" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-745">
                Mesero
              </span>
            </div>
            <div className="space-y-1" data-id="element-746">
              <p className="text-2xl font-bold text-blue-600" data-id="element-747">
                {channelBreakdown.waiter}
              </p>
              <p className="text-sm text-gray-500" data-id="element-748">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-749">
                  {waiterPct}%
                </span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-750">
                {formatCurrency(channelBreakdown.waiterRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}